import type { VocabularyOverride } from './vocabulary.types'

/**
 * Logistics vertical — the strings this vertical says differently from the neutral base. Selected at
 * build time by `VITE_VERTICAL=logistics` (`npm run build:logistics`, via `.env.logistics`).
 *
 * A deep-partial: name only what changes; everything omitted inherits the base. The core renames are
 * reader device → delivery agent device and sender → shipper; the sentences that carry those nouns are
 * overridden alongside the bare `terms` so the screens stay consistent. NOTE: `permissions` and `nav`
 * are arrays — they replace WHOLESALE, so the full list is restated here. To add another vertical,
 * copy this file to `vocabulary.<name>.ts`, add `.env.<name>`, and add one branch in `vocabulary.ts`.
 */
export const logistics: VocabularyOverride = {
  terms: {
    readerDevice: 'Delivery agent device',
    readerDevices: 'Delivery agent devices',
    readers: 'Delivery agents',
    sender: 'Shipper',
    senders: 'Shippers',
  },

  glossary: {
    published: {
      title: 'Labels published',
      body: 'Labels your shippers have sent. Only the organizations you sealed them to can read what is inside — we hold them and cannot open them.',
    },
    received: {
      title: 'Labels received',
      body: 'Labels other organizations sealed to you. Your delivery agent devices can open these; this counts what has arrived, not what has been opened.',
    },
    admin: {
      title: 'Admin device',
      body: 'A phone that approves delivery agent devices for your organization. Approving happens on the phone itself, so nobody here — including us — can let a device in.',
    },
    reader: {
      title: 'Delivery agent device',
      body: 'A handheld or phone a delivery agent uses to scan labels and read what is inside. It works once an admin device approves it, and stops the moment you revoke it.',
    },
    server: {
      title: 'Shipper',
      body: 'One of your systems that publishes labels. It creates its own credentials the first time it starts, so there is no password for you to store or rotate.',
    },
    risk: {
      title: 'Risk',
      body: 'A running score of how unusually this device is being used — higher means more concern: wrong delivery codes, reads far outside delivery areas, screenshot attempts, sudden re-enrollments, or a pace and shift unlike its own history. It looks at how the device behaves, never at what the labels say, and a high score never blocks anything by itself.',
    },
  },

  riskRules: {
    'wrong-otp-burst': 'Wrong delivery codes',
    'out-of-area': 'Reads outside the delivery area',
    'call-frequency': 'Repeated calls to one recipient',
    'odd-hours': 'Deliveries at unfamiliar hours',
  },

  permissions: [
    { slug: 'delegator-admin', label: 'Set up devices', hint: 'pair admin devices, invite delivery agent devices' },
    { slug: 'developer', label: 'Connect shippers', hint: 'add the systems that publish labels' },
    { slug: 'owner-admin', label: 'Manage the team', hint: 'invite people, change what they can do, remove them' },
  ],

  nav: [
    { to: '/home', label: 'Home' },
    { to: '/devices', label: 'Devices' },
    { to: '/servers', label: 'Shippers' },
    { to: '/team', label: 'Team' },
    { to: '/labels', label: 'Labels' },
  ],

  signIn: {
    pitch: 'Send from your systems. Approve the delivery agent devices that read. We hold what you send and cannot open it.',
  },

  home: {
    highRiskOne: 'A delivery agent device is flagged at high risk.',
    highRiskMany: 'delivery agent devices are flagged at high risk.',
    waitingOne: 'A delivery agent device is waiting for approval.',
    waitingMany: 'delivery agent devices are waiting for approval.',
    senderConnected: '✓ A shipper is connected',
    connectSender: 'Connect a shipper',
    allSetUp: '✓ Admin and delivery agent devices are set up',
  },

  devices: {
    noReaders: 'No delivery agent devices yet. Invite the first device that will read your labels.',
    getReaderApp: 'Get the delivery agent app',
    readerAppInstall: 'Scan with the phone to join the test programme on Google Play and install the delivery agent app — then come back here and invite it.',
    pairAdminFooter: 'An admin device approves delivery agent devices and can be used from anywhere.',
    inviteReaderTitle: 'Invite a delivery agent device',
    noAdmins: 'No admin devices yet. Pair one to approve the delivery agent devices that read your labels.',
  },

  servers: {
    loadError: 'Could not load your shippers.',
    connectSender: 'Connect a shipper',
    noSenders: 'No shippers yet. Connect the system that publishes your labels.',
    askOwner: 'Ask an owner to connect a shipper.',
    unnamedSender: 'Unnamed shipper',
    connectModalTitle: 'Connect a shipper',
    connectModalFooter: 'The shipper creates its own credentials the first time it starts. Nothing else to copy, and the setup code stops working once it is used.',
    testProdNote: 'Test and production are separate identities — a test shipper can never publish as production.',
    thenOnSender: 'Then, on the shipper',
  },
}
